import { retrieveFeaturedList } from '@/api/featured'
import { createSharedComposable } from '@vueuse/core'
import { metaPixelTrack } from '@/utils/utils'
import { useCurrencyStore } from '@/pinia/currency'

/**
 * @returns {object}
 * filter - 返回过滤项配置
 * loading - 当前是否处于加载中
 * finished - 是否已加载完成，加载完成后不再触发 onLoad 事件
 * list - 返回列表数据
 * onLoad - 加载更多
 * onReset - 重置 page / loading / finished 状态并触发 onLoad 事件
 */
export const useProductList = createSharedComposable(() => {
  const route = useRoute()
  const currency = useCurrencyStore()

  const loading = ref(false)
  const finished = ref(false)

  const category = computed(() => route.params.category)
  const seriesId = computed(() => route.params.collectionId)

  const filter = reactive({
    keyword: route.query.keyword,
    sortMap: null,
    priceStart: null,
    priceEnd: null
  })
  const page = reactive({
    pageNow: 1,
    pageSize: 50
  })

  const list = shallowRef([])

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const onLoad = async () => {
    if (loading.value || finished.value) return
    try {
      const currentRequestId = ++latestRequestId
      const { priceStart, priceEnd, ...rest } = filter
      const data = { ...rest, ...page }
      if (priceStart) {
        data.priceStart = priceStart / currency.current.rate
      }
      if (priceEnd) {
        data.priceEnd = priceEnd / currency.current.rate
      }
      loading.value = true
      const rows = await getProductList(data)

      if (currentRequestId === latestRequestId) {
        if (rows.length !== 0) {
          list.value = [...list.value, ...rows]
          page.pageNow++
        } else {
          finished.value = true
        }
        /**
         * 不用 finally 修改 loading 状态是因为 当 请求竞态 出现时，finally 会导致 loading 状态不正确
         */
        loading.value = false
      }
    } catch (e) {
      console.error(e)
      loading.value = false
    }
  }

  const getProductList = async (filter) => {
    const { code, data, msg } = await retrieveFeaturedList({
      ...filter,
      category: category.value,
      seriesId: seriesId.value,
      currency: currency.current.targetCurrency
    }, {
      failedRedirect: filter.pageNow === 1
    })
    if (code !== 200) {
      throw new Error(msg)
    }
    /**
     * Meta Pixel 埋点：Featured搜索完成
     */
    if (filter.pageNow === 1 && filter.searchKeyWord !== '' && filter.searchKeyWord != null) {
      metaPixelTrack('PFS401', 'Search', {
        content_ids: data.slice(0, 10).map((item) => item.offerId),
        content_type: 'product_group',
        currency: '$',
        value: 0.1,
        search_string: filter.searchKeyWord
      })
    }
    return data
  }

  const onReset = () => {
    page.pageNow = 1
    list.value = []
    loading.value = false
    finished.value = false
    onLoad()
  }

  onBeforeMount(() => {
    onLoad()
  })

  watch(() => [category.value, seriesId.value], () => {
    filter.keyword = route.query.keyword
    onReset()
  })

  return { filter, loading, finished, list, onLoad, onReset }
})
